import { Utils } from '../../utils/utils';
import { ICheckedEntity, MatchingResult } from '../models/abstractions';
import { IConstraint, IConstraintBase, IConstraintItem } from '../models/model.constraint';


/**
 * View Model per le proprietà sorgenti dei constraint.
 * All'interno di questa classe viene mantenuta l'associazione sorgente - lista di constraint scatenati dalla modifica di quella sorgente.
 */
export class PropConstraintView {
    /*
     * Identificativo della proprietà sorgente
     */
    propertyId: string;

    /**
     * Constraint associati alla proprietà sorgente
     */
    constraints: IConstraint[] = [];

    /**
     * Costruttore della classe.
     * @param propId Identificativo della proprietà sorgente
     */
    constructor(propId: string) {
        this.propertyId = propId;
    }
}

/**
 * View Model per la gestione della lista di colonne sorgenti dei constraint.
 */
export class ConstraintSources {
    /**
     * Array di sorgenti
     */
    constraintSources: PropConstraintView[] = [];

    /**
     * Aggiunge all'array una nuova associazione constraint-colonna sorgente.
     * @param constraint
     */
    public addConstraintSources(constraint: IConstraint | null) {
        if (!constraint) {
            throw new ReferenceError("Can't add a source with an undefined constraint.");
        }

        for (let constraintItem of constraint.constraintItems) {
            this.addConstraintSource(constraintItem.propertyId, constraint);

            if (!!constraintItem.matchPropertyId) this.addConstraintSource(constraintItem.matchPropertyId, constraint);
        }

        if (!!constraint.condition) {
            for (let constraintConditionItem of constraint.condition.constraintItems) {
                this.addConstraintSource(constraintConditionItem.propertyId, constraint);

                if (!!constraintConditionItem.matchPropertyId)
                    this.addConstraintSource(constraintConditionItem.matchPropertyId, constraint);
            }
        }
    }

    /**
     * Cerca una specifica colonna nell'array delle sorgenti. Se la trova, la ritorna.
     */
    public getSource(propertyId: string): PropConstraintView | undefined {
        return this.constraintSources.find((source) => {
            if (source.propertyId.toLowerCase() === propertyId.toLowerCase()) return true;
            return false;
        });
    }

    /**
     * Cerca una specifica colonna nell'array delle sorgenti. Se la trova, la ritorna.
     */
    public getSourcesByListName(listName: string): PropConstraintView[] {
        return this.constraintSources.filter((source) => {
            const sourceListName = Utils.getListNameFromPropId(source.propertyId);

            if (sourceListName.toLowerCase() === listName.toLowerCase()) return true;
            return false;
        });
    }

    /**
     * Cerca le sorgenti che rientrano sotto un certo path.
     *
     * @example sotto listPath = 'tbCardioExam.tbCsProc.tbCsValvReport', rientrano le eventuali sorgenti
     * 'tbCardioExam.tbCsProc.tbCsValvReport'
     * 'tbCardioExam.tbCsProc.tbCsValvReport.tbCsValvReportSheet.IsAorticValve'
     * 'tbCardioExam.tbCsProc.tbCsValvReport.tbCsValvReportSheet.IsReplacementTreatment'
     */
    public getSourcesInsideASpecificPath(listPath: string): PropConstraintView[] {
        return this.constraintSources.filter((source) => {
            const sourceNameCaseInsensitive = source.propertyId.toLowerCase();
            const listPathCaseInsensitive = listPath.toLowerCase();

            return (
                sourceNameCaseInsensitive === listPathCaseInsensitive ||
                sourceNameCaseInsensitive.startsWith(listPathCaseInsensitive + '.')
            );
        });
    }

    // #region privates

    /**
     * Aggiunge una colonna all'elenco delle sorgenti (se non è già presente nell'elenco) ed associa il constraint in input.
     */
    private addConstraintSource(propertyId: string, constraint: IConstraint) {
        if (!propertyId) {
            //la propertyId nulla non è più supportata (esiste il MatchPropertyId)
            console.error('Error adding a constraint source. propertyId has to be defined.');
            console.dir(constraint);

            throw new Error('Error adding a constraint source. propertyId has to be defined.');
        }

        let currentProperty: PropConstraintView | undefined = this.getSource(propertyId);
        if (!currentProperty) {
            currentProperty = new PropConstraintView(propertyId);
            this.constraintSources.push(currentProperty);
        }

        //alla fine aggiungo il constraint alla lista dei constraint che hanno come sorgente quella colonna
        currentProperty.constraints.push(constraint);

        return currentProperty;
    }

    // #endregion
}

/*
 * E' la classe che memorizza il risultato della verifica di un intero constraint,
 * includendo anche i risultati parziali dei suoi constraint items su ogni entity
 * coinvolta nel constraint.
 */
export class SeConstraintMatchingResult {
    constructor(
        public constraint: IConstraintBase | null,
        public matchingResult: MatchingResult | null /* null = skipped */,
        public partialResults: SeEntityMatchingResult[] | null,
    ) {}
}

/*
 * E' la classe che memorizza, per una entità, il risultato della verifica di un insieme di constraint item
 */
export class SeEntityMatchingResult {
    constructor(
        public checkedEntity: ICheckedEntity | null,
        public matchingResult: MatchingResult,
        public constraintItemResults: SeConstraintItemMatchingResult[] | null,
    ) {}
}

/**
 * E' la classe che memorizza, per una entità, il risultato della verifica di un constraint item
 * */
export class SeEntityConstraintItemResult {
    constructor(
        public checkedEntity: ICheckedEntity | undefined | null,
        public constraintItemResult: SeConstraintItemMatchingResult,
    ) {}
}

/*
 * E' la classe che associa il risultato della verifica ad un singolo contraint item
 */
export class SeConstraintItemMatchingResult {
    constructor(
        public constraintItem: IConstraintItem | null | undefined,
        public matchingResult: MatchingResult | null,
    ) {}
}
